import { ErrorState, LoadingRows } from "@/components/data-state";
import { PLAYBACK_MAX_POINTS } from "@/components/run-detail";
import { TrajectoryPlayer } from "@/components/trajectory-player";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAsync } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import type { SimulatorSide } from "@/lib/api";
import { adminApi } from "@/lib/api";

/**
 * A run's path for a TA, whatever the challenge's `show_trajectory` says: the admin
 * endpoints bypass it, so a run can be reviewed during a live competition without showing
 * students the walls.
 */
export function TrajectoryDialog({
  jobId,
  challengeId,
  simulator,
  onClose,
}: {
  jobId: string | null;
  challengeId: string;
  simulator: SimulatorSide | undefined;
  onClose: () => void;
}) {
  const adminKey = useAdminKey();

  const trajectory = useAsync(
    jobId && adminKey
      ? () => adminApi.trajectory(jobId, adminKey, PLAYBACK_MAX_POINTS)
      : null,
    [jobId, adminKey],
  );
  const walls = useAsync(
    jobId && adminKey ? () => adminApi.challengeWalls(challengeId, adminKey) : null,
    [challengeId, adminKey, jobId],
  );

  return (
    <Dialog open={jobId !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[92svh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Trajectory</DialogTitle>
          <DialogDescription className="font-mono text-xs">{jobId}</DialogDescription>
        </DialogHeader>

        {trajectory.error ? (
          <ErrorState error={trajectory.error} onRetry={trajectory.refetch} />
        ) : !trajectory.data ? (
          <LoadingRows rows={5} />
        ) : (
          <TrajectoryPlayer
            walls={walls.data ?? []}
            trajectory={trajectory.data}
            maxSize="calc(92svh - 230px)"
            timeUnit={simulator === "server" ? "steps" : "seconds"}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
